import { useCallback, useEffect, useRef } from 'react';

const BOTTOM_THRESHOLD_PX = 50;

export function isNearBottom(
  target: { scrollHeight: number; scrollTop: number; clientHeight: number },
  thresholdPx: number = BOTTOM_THRESHOLD_PX
): boolean {
  return target.scrollHeight - target.scrollTop - target.clientHeight <= thresholdPx;
}

/**
 * 追記型スクロール領域の末尾自動追従。ユーザーが末尾近くにいる時だけ追従する。
 * `stickToBottom()` は送信ボタン押下など、強制的に追従へ戻したい時に呼ぶ。
 */
export function useStickToBottomScroll<
  ContainerEl extends HTMLElement = HTMLDivElement,
  ContentEl extends HTMLElement = HTMLDivElement,
>() {
  const scrollContainerRef = useRef<ContainerEl | null>(null);
  const scrollContentRef = useRef<ContentEl | null>(null);
  const stuckToBottomRef = useRef(true);
  const scheduledFrameRef = useRef<number | null>(null);

  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  // scrollHeight の参照は同期レイアウトを強制し、同一ドキュメント上のキャンバスまで
  // 巻き込む。レンダーやリサイズのたびではなく次フレームに1回だけ実行する。
  const scheduleScrollToBottom = useCallback(() => {
    if (!stuckToBottomRef.current) return;
    if (scheduledFrameRef.current !== null) return;
    scheduledFrameRef.current = requestAnimationFrame(() => {
      scheduledFrameRef.current = null;
      if (!stuckToBottomRef.current) return;
      scrollToBottom();
    });
  }, [scrollToBottom]);

  useEffect(() => {
    return () => {
      if (scheduledFrameRef.current !== null) {
        cancelAnimationFrame(scheduledFrameRef.current);
        scheduledFrameRef.current = null;
      }
    };
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    stuckToBottomRef.current = isNearBottom(el);
  }, []);

  // 画像ロードなど React 再レンダリングを伴わない高さ変化を拾う
  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(() => {
      scheduleScrollToBottom();
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [scheduleScrollToBottom]);

  // ストリーミング更新の取りこぼし防止 (ResizeObserver のタイミング依存を補う)
  useEffect(() => {
    scheduleScrollToBottom();
  });

  const stickToBottom = useCallback(() => {
    stuckToBottomRef.current = true;
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  return { scrollContainerRef, scrollContentRef, onScroll, stickToBottom };
}
